import React, { cloneElement, ForwardedRef, forwardRef, isValidElement, MutableRefObject } from 'react';
import { NativeSyntheticEvent, StyleProp, TargetedEvent, TouchableHighlight, ViewStyle } from 'react-native';

import { Theme } from '@constants';
import { helpers } from '@helpers';
import { useNodeHandle, useNodeHandleFromIndex } from '@hooks';

import { FocusableHighlight, FocusableHighlightProps } from '../FocusableHighlight/FocusableHighlight';

export const FocusableFlatListItem = forwardRef<TouchableHighlight, FocusableFlatListItemProps>(
  (
    {
      item,
      references,
      wrapItem,
      horizontal,
      index,
      children,
      nextFocusDown,
      nextFocusLeft,
      nextFocusRight,
      nextFocusUp,
      itemStyle,
      itemStyleFocused,
      underlayColor,
      onFocus,
      isLast,
      isFirst,
    },
    reference,
  ) => {
    const thisHandle = useNodeHandleFromIndex(references, index);
    const noFocus = helpers.isAndroid ? thisHandle : null;
    const horizListUp = nextFocusUp === null ? noFocus : nextFocusUp;
    const horizListDown = nextFocusDown === null ? noFocus : nextFocusDown;
    const vertListLeft = nextFocusLeft === null ? noFocus : nextFocusLeft;
    const vertListRight = nextFocusRight === null ? noFocus : nextFocusRight;

    const itemNextHandle = useNodeHandleFromIndex(references, isLast ? undefined : index + 1);

    const itemPreviousHandle = useNodeHandleFromIndex(references, isFirst ? undefined : index - 1);

    const listNextHandle = useNodeHandle(horizontal ? nextFocusRight : nextFocusDown);

    const listPreviousHandle = useNodeHandle(horizontal ? nextFocusLeft : nextFocusUp);

    let nextHandle = isLast ? listNextHandle : itemNextHandle;
    let previousHandle = isFirst ? listPreviousHandle : itemPreviousHandle;

    const thisItemHandle = useNodeHandleFromIndex(references, index);

    if (nextHandle == null) {
      nextHandle = thisItemHandle;
    }
    if (previousHandle == null) {
      previousHandle = thisItemHandle;
    }

    if (!underlayColor) {
      underlayColor = Theme.white;
    }

    return wrapItem ? (
      <FocusableHighlight
        key={item.id}
        nativeID={`${item.id}`}
        nextFocusDown={horizontal ? horizListDown : nextHandle}
        nextFocusLeft={horizontal ? previousHandle : vertListLeft}
        nextFocusRight={horizontal ? nextHandle : vertListRight}
        nextFocusUp={horizontal ? horizListUp : previousHandle}
        ref={reference}
        style={itemStyle}
        styleFocused={itemStyleFocused}
        testID={`wrapped_list_item_${item.id}`}
        underlayColor={underlayColor}
        onFocus={onFocus}
        onPress={() => {}}
      >
        {children}
      </FocusableHighlight>
    ) : isValidElement<ClonedElement>(children) ? (
      cloneElement<ClonedElement>(children, {
        key: item.id,
        nextFocusDown: horizontal ? horizListDown : nextHandle,
        nextFocusLeft: horizontal ? previousHandle : vertListLeft,
        nextFocusRight: horizontal ? nextHandle : vertListRight,
        nextFocusUp: horizontal ? horizListUp : previousHandle,
        ref: reference,
        onFocus,
      })
    ) : null;
  },
);

type FocusableFlatListItemProps = FocusableHighlightProps & {
  item: { id: string };
  index: number;
  wrapItem: boolean;
  horizontal?: boolean | null;
  itemStyle?: StyleProp<ViewStyle>;
  itemStyleFocused?: StyleProp<ViewStyle>;
  isLast?: boolean;
  isFirst?: boolean;
  references: MutableRefObject<Record<number, TouchableHighlight | null>>;
};

type ClonedElement = {
  key: string;
  nextFocusDown?: number | null;
  nextFocusLeft?: number | null;
  nextFocusRight?: number | null;
  nextFocusUp?: number | null;
  ref: ForwardedRef<TouchableHighlight>;
  onFocus?: (e: NativeSyntheticEvent<TargetedEvent>) => void;
};
